<script setup lang="ts">
import { toRefs, computed } from "vue";
import SegmentPartDO = App.Data.Segments.SegmentPartDO;
import SegmentFilter from "@/modules/segments/components/SegmentFilter.vue";
import SegmentGroup from "@/modules/segments/components/SegmentGroup.vue";
import { useI18n } from "vue-i18n";
import { ucfirst } from "chartjs-graph";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
const { t } = useI18n();
import { useSegmentBuilderStore } from "@/modules/segments/store";
import { loadNewPart } from "@/modules/segments/pages/SegmentUtils";
import SegmentSummaryFilter from "@/modules/segments/components/SegmentSummaryFilter.vue";
import SegmentPartType = App.Enums.SegmentPartType;
import useDialog from "@/ui/composables/useDialog";
const segmentBuilderState = useSegmentBuilderStore().segmentBuilderState;

const props = defineProps({
	dataSource: String,
	showDataSourceGrouping: Boolean,
	isExpertMode: Boolean,
});

const { dataSource, showDataSourceGrouping, isExpertMode } = toRefs(props);

const emit = defineEmits(["add-dimension-filter", "add-metric-filter"]);

const availablePartTypes = ["include", "exclude"];

const showAddOptionForDataSource = (
	dataSource: string,
	partType: "include" | "exclude"
): boolean => {
	return segmentBuilderState.segment.parts.every(
		(part) => !(part.data_source === dataSource && part.type === partType)
	);
};

const addPartOnDataSource = (
	dataSource: string,
	partType: SegmentPartType
): void => {
	segmentBuilderState.segment.parts.push(loadNewPart(dataSource, partType));
};

const removePartFromSegment = async (dataSource: string, partType: string) => {
	const confirm = await useDialog().confirm({
		title: "Delete Part",
		message: t("segments.deletePart"),
		isDelete: true,
	});

	if (!confirm) {
		return;
	}

	// cant use .filter to update the parts
	for (let i = segmentBuilderState.segment.parts.length - 1; i >= 0; i--) {
		if (
			segmentBuilderState.segment.parts[i].data_source === dataSource &&
			segmentBuilderState.segment.parts[i].type === partType
		) {
			segmentBuilderState.segment.parts.splice(i, 1);
		}
	}
};

const removeFilterFromPart = (
	part: SegmentPartDO,
	activeFilter: ActiveFilterDTO
) => {
	const index = part.active_filters.findIndex(
		(filter) =>
			filter.dimension.name_clean === activeFilter.dimension.name_clean
	);

	if (index !== -1) {
		part.active_filters.splice(index, 1);
	}
};

const partsWithIndex = computed(() => {
	return segmentBuilderState.segment.parts.map((part, index) => ({
		...part,
		index,
	}));
});

const computeParts = computed(() =>
	partsWithIndex.value
		.filter((part) => part.data_source === dataSource.value)
		.sort((a, b) => (a.type === "include" ? -1 : b.type === "include" ? 1 : 0))
);

const addFilter = (filterType: string, partType: string) => {
	emit(`add-${filterType}-filter`, partType);
};

const removeMetricFilter = (partType: string) => {
	const part = segmentBuilderState.segment.parts.find(
		(part) => part.data_source === "tab_occ_prk_v" && part.type === partType
	);

	part.metric = null;
	part.operator = null;
	part.values = null;
};
</script>

<template>
	<template v-if="(!isExpertMode && computeParts.length > 0) || isExpertMode">
		<segment-group
			:label="`Select from source: ` + t(`segments.` + dataSource)"
			:state="showDataSourceGrouping ? 'data-source' : 'hidden'"
		>
			<segment-group
				v-for="part in computeParts"
				:key="part.id"
				:label="`Group: ` + ucfirst(part.type)"
				:state="isExpertMode ? part.type : 'hidden'"
				:label-icon="'mdi-trash-can-outline'"
				:label-action="() => removePartFromSegment(dataSource, part.type)"
			>
				<div v-if="dataSource === 'tab_occ_prk_v'" class="d-flex w-100 pb-5">
					<v-btn
						v-if="!part?.metric && !part?.operator"
						variant="tonal"
						density="compact"
						@click="addFilter('metric', part.type)"
						:color="part.type === 'exclude' ? 'active-secondary' : 'primary'"
					>
						Define a 'Summary Condition'
						<v-tooltip activator="parent" location="bottom start">
							In order to set a single, overarching metric filter for this
							group.
						</v-tooltip>
					</v-btn>
					<template v-else>
						<segment-summary-filter
							:part="part"
							@edit-metric-filter="addFilter('metric', part.type)"
							@remove-metric-filter="removeMetricFilter(part.type)"
						/>
					</template>
				</div>
				<div
					class="px-3"
					v-for="(usedFilter, index) in part.active_filters"
					:key="index + usedFilter.dimension.name_clean"
				>
					<segment-filter
						:used-filter="usedFilter"
						:part-type="part.type"
						:part-idx="part.index"
						:filter-idx="index"
						@remove-filter="() => removeFilterFromPart(part, usedFilter)"
					/>
				</div>
				<div class="d-flex">
					<v-spacer />
					<v-btn
						variant="tonal"
						density="compact"
						:color="part.type === 'include' ? 'primary' : 'active-secondary'"
						@click="addFilter('dimension', part.type)"
					>
						Add Filter
					</v-btn>
					<v-spacer />
				</div>
			</segment-group>
			<template
				v-for="(availablePartType, index) in availablePartTypes"
				:key="index"
			>
				<div
					class="d-flex"
					style=""
					v-if="
						showAddOptionForDataSource(dataSource, availablePartType) &&
						isExpertMode
					"
				>
					<v-spacer></v-spacer>
					<div>
						<v-btn
							variant="text"
							density="compact"
							:color="
								availablePartType === 'exclude' ? 'active-secondary' : 'active'
							"
							@click="addPartOnDataSource(dataSource, availablePartType)"
						>
							Add an
							{{ availablePartType === "exclude" ? "exclusion" : "inclusion" }}
							group
						</v-btn>
					</div>
				</div>
			</template>
		</segment-group>
	</template>
</template>

<style scoped lang="scss"></style>
